import React from 'react';
import { LuCheck, LuGlobe, LuLoader, LuText, LuFileUp, LuTriangleAlert } from 'react-icons/lu';
import {
  importFootnote,
  importKicker,
  importSubtitle,
  importTitleInProgress,
} from '../../utils/importSourceLabels';

const STAGE_STEPS = [
  { key: 'UPLOADED', label: 'Uploaded' },
  { key: 'QUEUED', label: 'Queue' },
  { key: 'EXTRACTING', label: 'Extract' },
  { key: 'ANALYZING', label: 'Analyze' },
  { key: 'GENERATING', label: 'Generate' },
  { key: 'VALIDATING', label: 'Validate' },
  { key: 'PENDING_REVIEW', label: 'Review' },
  { key: 'COMPLETED', label: 'Done' },
];

const STAGE_LABELS = {
  UPLOADED: 'Upload received',
  EXTRACTING: 'Extracting text',
  EXTRACTING_OCR: 'Running OCR on scanned pages…',
  ANALYZING: 'Analyzing structure',
  GENERATING: 'Generating questions',
  VALIDATING: 'Validating quality',
  PENDING_REVIEW: 'Ready for your review',
  INDEXING: 'Indexing cards',
  COMPLETED: 'Finishing up',
  QUEUED: 'Waiting in queue',
};

function stepIndex(stage) {
  const normalized = stage === 'EXTRACTING_OCR' ? 'EXTRACTING' : stage;
  const i = STAGE_STEPS.findIndex((s) => s.key === normalized);
  return i >= 0 ? i : 0;
}

function stageLabel(stage, status) {
  if (stage && STAGE_LABELS[stage]) return STAGE_LABELS[stage];
  if (status && STAGE_LABELS[status]) return STAGE_LABELS[status];
  return 'Working…';
}

function SourceIcon({ job }) {
  const type = (job?.sourceType || '').toUpperCase();
  if (type === 'URL') return <LuGlobe />;
  if (type === 'TEXT') return <LuText />;
  return <LuFileUp />;
}

export default function LearnImportProgress({ job, onRetry, onDismiss }) {
  if (!job) return null;

  const kicker = importKicker(job);

  if (job.status === 'FAILED') {
    return (
      <div className="learn-import-panel learn-import-panel--error" role="alert">
        <div className="learn-import-head">
          <span className="learn-import-icon learn-import-icon--error" aria-hidden>
            <LuTriangleAlert />
          </span>
          <div className="learn-import-head-text">
            <p className="learn-import-kicker">{kicker}</p>
            <p className="learn-import-title">Import incomplete</p>
            <p className="learn-import-sub">{job.errorMessage || 'Processing stopped before completion.'}</p>
            {job.questionsGenerated > 0 && (
              <p className="learn-import-meta">{job.questionsGenerated} questions were kept in this notebook.</p>
            )}
          </div>
        </div>
        {onRetry && (
          <div className="learn-import-actions">
            <button type="button" className="home-btn home-btn-primary learn-btn--compact" onClick={onRetry}>
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  if (job.status === 'PENDING_REVIEW' || job.stage === 'PENDING_REVIEW') {
    return (
      <div className="learn-import-panel learn-import-panel--active" role="status">
        <div className="learn-import-head">
          <span className="learn-import-icon learn-import-icon--active" aria-hidden>
            <LuLoader className="learn-import-spinner" />
          </span>
          <div className="learn-import-head-text">
            <p className="learn-import-kicker">{kicker}</p>
            <p className="learn-import-title">Questions ready for review</p>
            <p className="learn-import-sub">
              {job.questionsGenerated} question{job.questionsGenerated === 1 ? '' : 's'} generated.
              Use the review panel below to add them to this notebook or discard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (job.status === 'COMPLETED') {
    return (
      <div className="learn-import-panel learn-import-panel--success">
        <div className="learn-import-head">
          <span className="learn-import-icon learn-import-icon--success" aria-hidden>
            <LuCheck />
          </span>
          <div className="learn-import-head-text">
            <p className="learn-import-kicker">{kicker}</p>
            <p className="learn-import-title">Import complete</p>
            <p className="learn-import-sub">
              {job.questionsGenerated} question{job.questionsGenerated === 1 ? '' : 's'} added to this notebook.
            </p>
            <p className="learn-import-meta">{importSubtitle(job)}</p>
          </div>
        </div>
        {onDismiss && (
          <div className="learn-import-actions">
            <button type="button" className="home-btn home-btn-secondary learn-btn--compact" onClick={onDismiss}>
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  }

  if (job.status === 'CANCELLED') {
    return (
      <div className="learn-import-panel learn-import-panel--muted">
        <div className="learn-import-head">
          <div className="learn-import-head-text">
            <p className="learn-import-kicker">{kicker}</p>
            <p className="learn-import-title">Import cancelled</p>
          </div>
        </div>
      </div>
    );
  }

  const stageKey = job.status === 'QUEUED' ? 'QUEUED' : job.stage;
  const activeIdx = stepIndex(stageKey);
  const pct = Math.min(100, Math.max(0, job.progress || 0));
  const currentLabel = stageLabel(stageKey, job.status);
  const questionsSoFar = Number(job.questionsGenerated) || 0;
  const preparingReview = job.stage === 'VALIDATING' && pct >= 88 && questionsSoFar > 0;

  return (
    <div className="learn-import-panel learn-import-panel--active" aria-live="polite">
      <div className="learn-import-head">
        <span className="learn-import-icon learn-import-icon--active" aria-hidden>
          <LuLoader className="learn-import-spinner" />
        </span>
        <div className="learn-import-head-text">
          <p className="learn-import-kicker">{kicker}</p>
          <p className="learn-import-title">{importTitleInProgress(job)}</p>
          <p className="learn-import-sub learn-import-source-line">{importSubtitle(job)}</p>
          <p className="learn-import-sub">
            <span className="learn-import-stage-name">
              {preparingReview ? 'Preparing review…' : currentLabel}
            </span>
            {questionsSoFar > 0 && (
              <>
                <span className="learn-import-sub-sep" aria-hidden>·</span>
                <span>{questionsSoFar} question{questionsSoFar === 1 ? '' : 's'} so far</span>
              </>
            )}
          </p>
          {preparingReview && (
            <p className="learn-import-meta">
              Almost done — packaging your preview. If this lasts more than a minute, refresh the page.
            </p>
          )}
          {job.queueMessage && (
            <p className="learn-import-meta learn-import-queue-msg">{job.queueMessage}</p>
          )}
          {job.pageFrom != null && job.pageTo != null && (
            <p className="learn-import-meta">
              Pages {job.pageFrom}–{job.pageTo}
            </p>
          )}
        </div>
        <div className="learn-import-pct" aria-hidden>
          <span className="learn-import-pct-value">{pct}</span>
          <span className="learn-import-pct-suffix">%</span>
        </div>
      </div>

      <div
        className="learn-progress learn-progress--import"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Import progress ${pct} percent`}
      >
        <span className="learn-progress-fill" style={{ width: `${pct}%` }} />
      </div>

      <ol className="learn-import-stepper">
        {STAGE_STEPS.map((step, idx) => {
          const done = idx < activeIdx;
          const current = idx === activeIdx;
          let state = 'pending';
          if (done) state = 'done';
          else if (current) state = 'active';
          return (
            <li key={step.key} className={`learn-import-step is-${state}`}>
              <span className="learn-import-step-dot" />
              <span className="learn-import-step-label">{step.label}</span>
            </li>
          );
        })}
      </ol>

      <p className="learn-import-foot">
        <SourceIcon job={job} aria-hidden />
        {importFootnote(job)}
      </p>
    </div>
  );
}
